import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Popconfirm } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import cart from 'redux/cart';
import invoice from 'redux/invoice';
import Table from 'components/molecules/Table';
import TableSearch from 'components/molecules/TableSearch';
import { columns } from './tableColumns';
import CartModal from 'components/molecules/CartModal';
import useCart from 'hooks/useCart';
import { lineTotal, quantityEdit } from 'utils/cartQuantity';

import ButtonSection from './components/ButtonSection';
import CheckoutBar from './components/CheckoutBar';
import DeliveryScheduleModal from './components/DeliveryScheduleModal';
import { needsDeliverySchedule as needsSchedule, hasPendingOrders } from './deliverySchedule';

import './index.scss';

const Cart = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('cart');
  const cartItems = useSelector(cart.selectors.selectAll);
  const cartItemsIds = useSelector(cart.selectors.selectIds);
  const cartTotal = useSelector(cart.selectors.getCartTotal);
  const pendingSummary = useSelector(invoice.selectors.getPendingSummary);
  const { onModalClose, cartLoader, cartAlert } = useCart();
  const [name, setName] = useState('');
  const [schedulingDelivery, setSchedulingDelivery] = useState(false);
  /* Tracked separately from cartLoader, which an unrelated refetch or row delete
     also raises - that would lock the dialog's confirm button behind a spinner. */
  const [submittingDelivery, setSubmittingDelivery] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');
  const [pendingOrdersAtSubmit, setPendingOrdersAtSubmit] = useState(false);
  /* The submit button now waits on a round trip before anything appears, so it
     says so - otherwise a click on a large order looks like it did nothing. */
  const [checkingSchedule, setCheckingSchedule] = useState(false);

  /* The cart is held whole in the browser, so this one filters in place - no
     request, and the totals below still count every line, not the filtered view. */
  const visibleItems = name ? cartItems.filter((item) => item.arabic_name?.includes(name)) : cartItems;

  useEffect(() => {
    dispatch(cart.thunks.fetch({ show_all: true }));
    /* Orders already waiting count toward the same delivery, so the cart cannot
       decide on its own contents alone. */
    dispatch(invoice.thunks.fetchPendingSummary());
  }, [dispatch]);

  /* Only decides whether the submit button asks its own confirmation - the
     decision that matters is remade against the API in onSubmit below. */
  const needsDeliverySchedule = useMemo(() => needsSchedule(cartItems, pendingSummary), [cartItems, pendingSummary]);

  /* Refreshed after the submit as well: the order just sent is now one of the
     pending ones, and the next order has to be judged against it. */
  const submitUnscheduled = () => {
    dispatch(cart.thunks.submitCart({ parts: cartItems }))
      .then(unwrapResult)
      .then(() => dispatch(invoice.thunks.fetchPendingSummary()))
      /* A refused submit can still have changed the cart: the API takes out
         lines it can no longer supply as it reads them. Without this the screen
         keeps showing rows that are already gone, and every retry is refused
         the same way until the client reloads the page. The scheduled path
         below refetches for the same reason. */
      .catch((error) => {
        /* The refusal is put back once the refetch settles. Starting a load
           clears the alert, as it should for a load the client asked for - but
           this one is a consequence of the failure, and without this the screen
           quietly corrects itself and never says why it changed. */
        const alert = cart.alertFromRejection(error);

        dispatch(cart.thunks.fetch({ show_all: true }))
          .then(unwrapResult)
          .then(() => alert && dispatch(cart.actions.setAlert(alert)))
          /* Only once the refetch came back. A refetch that failed has said so
             itself, through the rejected matcher, and putting the submit's
             refusal back over it would leave a client in network trouble
             reading about the order they tried rather than the load that just
             failed under them. */
          .catch(() => {});
      });
  };

  /* Every order is confirmed exactly once. The popconfirm on the button does it
     for most, and the scheduling dialog does it for the rest - but which of the
     two a click gets is settled from the store, before the check below can
     disagree. When it does disagree the other way round - the button skipped its
     popconfirm expecting a scheduling dialog that the API then says is not
     needed - this stands in, so the order is never sent on a bare click. */
  const confirmThenSubmitUnscheduled = () => {
    Modal.confirm({
      title: t('submit-confirmation'),
      okText: t('yes'),
      cancelText: t('no'),
      /* Called imperatively, so it renders outside the tree and picks up no
         ConfigProvider - the same reason the scheduling dialog carries its own. */
      direction: 'rtl',
      onOk: submitUnscheduled,
    });
  };

  /* The count is remade against the API whichever way the store leans, rather
     than only when it says no. What is in the store is as fresh as the last
     fetch, and the client arrives here from the "go to cart" button in the
     add-to-cart dialog, submits, adds more and comes back, all without the
     screen ever reloading - so it can be stale in both directions: a delivery
     that has grown past the threshold since, or one whose pending orders have
     been handed over and no longer count. Asking for a delivery date on a cart
     of three parts with nothing waiting behind it reads as a bug to the client.

     A failed check submits rather than blocking the order. */
  const onSubmit = () => {
    // Read before the round trip, so it reflects the button that was clicked.
    const alreadyConfirmed = !needsDeliverySchedule;
    const submitOrAsk = () => (alreadyConfirmed ? submitUnscheduled() : confirmThenSubmitUnscheduled());

    setCheckingSchedule(true);
    dispatch(invoice.thunks.fetchPendingSummary())
      .then(unwrapResult)
      .then((summary) => {
        if (needsSchedule(cartItems, summary)) {
          /* Taken from the response rather than the store, which has not
             committed yet - the dialog would otherwise show the wrong hint on a
             small cart that only crossed the line because of pending orders. */
          setPendingOrdersAtSubmit(hasPendingOrders(summary));
          setSchedulingDelivery(true);
        } else submitOrAsk();
      })
      .catch(submitOrAsk)
      .finally(() => setCheckingSchedule(false));
  };

  /* The dialog stays up until the submit settles so it can show progress and, on
     failure, keep the date the client picked. The cart is refetched after a
     failure because the API may no longer be holding what the client is looking
     at: a line it can no longer supply is dropped from the cart as the order is
     attempted, so a retry has to be sent against what is actually there. */
  const onDeliveryConfirm = (deliveryAt) => {
    setSubmittingDelivery(true);
    setDeliveryError('');
    dispatch(cart.thunks.submitCart({ parts: cartItems, deliveryAt }))
      .then(unwrapResult)
      .then(() => {
        setSchedulingDelivery(false);
        // The submitted order is now one of the pending ones.
        dispatch(invoice.thunks.fetchPendingSummary());
        setSubmittingDelivery(false);
      })
      .catch((error) => {
        /* The dialog carries the error itself; the cart's own alert is held back
           until it closes. Read the same way the alert is, or this path would
           miss the refusal keyed by the field it refused - the shape this whole
           change is about - and answer a quantity the API named with a generic
           failure of its own. Its own message stands in only when the API gave
           no readable reason; what is set here is rendered as text. */
        setDeliveryError(cart.rejectionMessage(error) || t('delivery-failed'));
        /* Pulled back before the button frees up, so the retry goes out against
           the cart the API is holding. A failed fetch does empty the store, and
           then there is nothing left to retry against - so the dialog closes
           rather than offering one, and the cart's own alert carries the error. */
        dispatch(cart.thunks.fetch({ show_all: true }))
          .then(unwrapResult)
          .catch(() => {
            setSchedulingDelivery(false);
            setDeliveryError('');
          })
          .finally(() => setSubmittingDelivery(false));
      });
  };

  const onCancel = () => {
    dispatch(cart.thunks.removeItems({ ids: cartItemsIds, cancel: true }));
  };

  const onDelete = (record) => () => {
    dispatch(cart.thunks.removeItems({ ids: [record.id], total: lineTotal(record) }));
  };

  const extraColumns = [
    ...columns,
    {
      title: 'delete',
      render: (_, record) => (
        <Popconfirm title={t('delete-confirm')} cancelText={t('no')} okText={t('yes')} onConfirm={onDelete(record)}>
          <Button type="primary" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  /* Zero used to be sent to the API as a quantity, which refuses anything below
     one, so the client got a generic failure and - until the store stopped
     emptying itself on a refused write - a cart that blanked itself as well. It
     is the removal it reads as instead; see `quantityEdit` for what each edit
     means and why a cleared box is not a zero. */
  const updateTable = (record) => {
    const edit = quantityEdit(record.quantity);
    if (edit === 'ignore') return;

    if (edit === 'remove') {
      /* `record` already carries the new quantity, so what has to come off the
         total is read from the line as the store still holds it. */
      dispatch(
        cart.thunks.removeItems({
          ids: [record.id],
          total: lineTotal(cartItems.find((item) => item.id === record.id)),
        }),
      );
      return;
    }

    dispatch(cart.thunks.addItems({ items: [record] }));
  };

  /* Sits under the last line of the order, where a client who has just checked
     the list decides whether anything is missing. */
  const addItemsRow = () => (
    <Link to="/makers" className="cart-add-items">
      <PlusOutlined />
      <span>{t('add-items')}</span>
    </Link>
  );

  return (
    <div className="cart-screen" title={t('your-cart')}>
      {/* Held back while the scheduling dialog is up. A rejected submit sets the
          cart's alert (see the rejected matcher in redux/cart), which would
          otherwise stack a second modal behind a dialog that is already showing
          the same failure inline. It surfaces as normal once the dialog closes,
          which is what carries the error on the paths that give up. */}
      <CartModal
        alert={schedulingDelivery ? null : cartAlert}
        onCancel={onModalClose}
        total={cartTotal}
        hideExtraButtons
      />
      <DeliveryScheduleModal
        visible={schedulingDelivery}
        onCancel={() => {
          setSchedulingDelivery(false);
          // Otherwise a failure from last time greets the next attempt.
          setDeliveryError('');
        }}
        onConfirm={onDeliveryConfirm}
        confirmLoading={submittingDelivery}
        submitError={deliveryError}
        includesPendingOrders={pendingOrdersAtSubmit}
      />
      {/* The cart is fetched whole - checkout submits every line, not the page
          on screen - so the table pages through it locally rather than asking
          the API for a slice. */}
      <TableSearch className="table-search--block" defaultValue={name} onSearch={setName} />
      <Table
        columns={extraColumns}
        dataSource={visibleItems}
        updateDataSource={updateTable}
        rowKey="id"
        localePath="table-fields"
        loading={cartLoader}
        footer={addItemsRow}
        pagination={{}}
      />
      <CheckoutBar total={cartTotal} itemsCount={cartItems.length}>
        <ButtonSection
          submitTitle={t('submit-title')}
          cancelTitle={t('cancel-title')}
          onSubmit={onSubmit}
          onCancel={onCancel}
          submitConfirmText={t('submit-confirmation')}
          /* The scheduling dialog is its own confirmation, so the popconfirm
             would ask twice for the orders that open it. */
          skipSubmitConfirm={needsDeliverySchedule}
          submitLoading={checkingSchedule}
          confirmText={t('cancel-confirmation')}
          cancelText={t('no')}
          okText={t('yes')}
          disabled={cartTotal === 0}
        />
      </CheckoutBar>
    </div>
  );
};

export default Cart;
